// src/components/Auth/Register.js
import React, { useState } from 'react';
import { 
  createUserWithEmailAndPassword, 
  updateProfile, 
  sendEmailVerification
} from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { auth, db } from '../../services/firebase';

const Register = ({ setCurrentView }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    contact: '',
    whatsapp: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const validateForm = () => {
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    const phoneRegex = /^\+?[1-9]\d{1,14}$/; // E.164 format

    if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required';
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    if (!formData.contact) {
      newErrors.contact = 'Contact number is required';
    } else if (!phoneRegex.test(formData.contact)) {
      newErrors.contact = 'Please enter a valid phone number with country code (e.g., +26612345678)';
    }
    if (!formData.whatsapp) {
      newErrors.whatsapp = 'WhatsApp number is required';
    } else if (!phoneRegex.test(formData.whatsapp)) {
      newErrors.whatsapp = 'Please enter a valid WhatsApp number with country code';
    }
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (!passwordRegex.test(formData.password)) {
      newErrors.password = 'Password must contain at least 8 characters, including uppercase, lowercase, number and special character';
    }
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      // Create user account
      const userCredential = await createUserWithEmailAndPassword(
        auth, 
        formData.email, 
        formData.password
      );
      
      // Update user profile with display name
      await updateProfile(userCredential.user, {
        displayName: formData.fullName
      });

      // Send email verification
      await sendEmailVerification(userCredential.user);

      // Save additional user data to Firestore
      await setDoc(doc(db, 'users', userCredential.user.uid), {
        fullName: formData.fullName,
        email: formData.email,
        contact: formData.contact,
        whatsapp: formData.whatsapp,
        emailVerified: false,
        phoneVerified: false, // Will be manually verified by admin
        role: 'user',
        createdAt: new Date(),
        status: 'pending_verification'
      });

      setEmailSent(true);
      
    } catch (error) {
      console.error('Registration error:', error);
      if (error.code === 'auth/email-already-in-use') {
        setErrors({ general: 'This email address is already registered. Please use a different email or try logging in.' });
      } else if (error.code === 'auth/weak-password') {
        setErrors({ general: 'Password is too weak. Please use a stronger password.' });
      } else {
        setErrors({ general: `Registration failed: ${error.message}` });
      }
    }
    setLoading(false);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    
    // Clear errors when user starts typing
    if (errors[e.target.name]) {
      setErrors({
        ...errors,
        [e.target.name]: undefined
      });
    }
    
    if (errors.general) {
      setErrors({
        ...errors,
        general: undefined
      });
    }
  };

  if (emailSent) {
    return (
      <div className="auth-container">
        <div className="success-message">
          <h2>Registration Successful! 🎉</h2>
          <div className="verification-info">
            <h3>Email Verification Sent</h3>
            <p>We've sent a verification email to <strong>{formData.email}</strong>.</p>
            <p>Please check your inbox and click the verification link to activate your account.</p>
            
            <div className="verification-tips">
              <h4>Didn't receive the email?</h4>
              <ul>
                <li>Check your spam or junk folder</li>
                <li>Wait a few minutes - emails can sometimes be delayed</li>
                <li>Make sure you entered the correct email address</li>
                <li>Contact support if you continue to have issues</li>
              </ul>
            </div>
          </div>
          
          <button 
            onClick={() => setCurrentView('login')} 
            className="btn-primary"
          >
            Continue to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <h2>Create Account</h2>
      <form onSubmit={handleSubmit} className="auth-form">
        <div className="form-group">
          <label>Full Name *</label>
          <input
            type="text"
            name="fullName"
            value={formData.fullName}
            onChange={handleChange}
            className={errors.fullName ? 'error' : ''}
          />
          {errors.fullName && <span className="error-text">{errors.fullName}</span>}
        </div>

        <div className="form-group">
          <label>Email *</label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className={errors.email ? 'error' : ''}
          />
          {errors.email && <span className="error-text">{errors.email}</span>}
        </div>

        <div className="form-group">
          <label>Contact Number *</label>
          <input
            type="tel"
            name="contact"
            value={formData.contact}
            onChange={handleChange}
            placeholder="+26612345678"
            className={errors.contact ? 'error' : ''}
          />
          {errors.contact && <span className="error-text">{errors.contact}</span>}
          <small className="input-hint">Include country code (e.g., +266 for Lesotho)</small>
        </div>

        <div className="form-group">
          <label>WhatsApp Number *</label>
          <input
            type="tel"
            name="whatsapp"
            value={formData.whatsapp}
            onChange={handleChange}
            placeholder="+26612345678"
            className={errors.whatsapp ? 'error' : ''}
          />
          {errors.whatsapp && <span className="error-text">{errors.whatsapp}</span>}
          <small className="input-hint">Include country code (e.g., +266 for Lesotho)</small>
        </div>

        <div className="form-group">
          <label>Password *</label>
          <div className="password-input">
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              value={formData.password}
              onChange={handleChange}
              className={errors.password ? 'error' : ''}
            />
            <button 
              type="button" 
              className="toggle-password"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? 'Hide' : 'Show'}
            </button>
          </div>
          {errors.password && <span className="error-text">{errors.password}</span>}
          <small className="input-hint">
            Must include uppercase, lowercase, number, and special character
          </small>
        </div>

        <div className="form-group">
          <label>Confirm Password *</label>
          <input
            type="password"
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleChange}
            className={errors.confirmPassword ? 'error' : ''}
          />
          {errors.confirmPassword && <span className="error-text">{errors.confirmPassword}</span>}
        </div>

        {errors.general && (
          <div className="error-text general-error">
            {errors.general}
          </div>
        )}

        <button type="submit" disabled={loading} className="btn-primary">
          {loading ? 'Creating Account...' : 'Register'}
        </button>
      </form>

      <p>
        Already have an account?{' '}
        <span className="auth-link" onClick={() => setCurrentView('login')}>
          Login here
        </span>
      </p>
    </div>
  );
};

export default Register;